/**
 * Sticky Add-to-Cart Component for Mobile
 * Shows a fixed bar at the bottom when the main ATC button scrolls out of view.
 */

import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

const ADDED_ANIMATION_DURATION = 2000;

class StickyAtcComponent extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #atcButton = null;

  /** @type {HTMLButtonElement | null} */
  #buyNowButton = null;

  /** @type {HTMLElement | null} */
  #priceElement = null;

  /** @type {number | undefined} */
  #addedTimeout;

  /** @type {boolean} */
  #isSubmitting = false;

  connectedCallback() {
    this.#atcButton = this.querySelector('[data-sticky-atc-button]');
    this.#buyNowButton = this.querySelector('[data-sticky-buy-now]');
    this.#priceElement = this.querySelector('[data-sticky-atc-price]');

    this.#setupObserver();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abortController.abort();
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
  }

  #setupObserver() {
    // Find the main product form's ATC button to observe
    const mainAtcButton = document.querySelector(
      'product-form-component [ref="addToCartButton"], .product-form-buttons button[type="submit"]'
    );

    if (!mainAtcButton) {
      console.warn('Sticky ATC: Could not find main Add to Cart button');
      return;
    }

    // Create intersection observer
    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Show sticky bar when main ATC is NOT visible
          if (entry.isIntersecting) {
            this.classList.remove('is-visible');
          } else {
            this.classList.add('is-visible');
          }
        });
      },
      {
        root: null,
        rootMargin: '-60px 0px 0px 0px', // Account for sticky header
        threshold: 0,
      }
    );

    this.#observer.observe(mainAtcButton);
  }

  #setupEventListeners() {
    const { signal } = this.#abortController;

    // Listen for variant changes
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Add to Cart button click
    this.#atcButton?.addEventListener('click', this.#handleAtcClick, { signal });

    // Buy Now button click
    this.#buyNowButton?.addEventListener('click', this.#handleBuyNowClick, { signal });
  }

  /**
   * Handle Add to Cart click - trigger the main product form
   */
  #handleAtcClick = async (event) => {
    event.preventDefault();

    if (this.#isSubmitting || this.#atcButton?.disabled) return;

    // Find and submit the main product form
    const productForm = document.querySelector('product-form-component form[data-type="add-to-cart-form"]');

    if (productForm) {
      // Trigger the form's submit handler
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      productForm.dispatchEvent(submitEvent);

      // Animate the sticky button
      this.#animateAdded();
    }
  };

  /**
   * Handle Buy Now click - add to cart then redirect to checkout
   */
  #handleBuyNowClick = async (event) => {
    event.preventDefault();

    if (this.#isSubmitting || this.#buyNowButton?.disabled) return;

    this.#isSubmitting = true;
    this.#buyNowButton?.classList.add('is-loading');

    try {
      // Get the current variant ID from the main form
      const variantInput = document.querySelector('product-form-component input[name="id"]');
      const quantityInput = document.querySelector('product-form-component input[name="quantity"]');

      if (!variantInput?.value) {
        console.error('Sticky ATC: No variant ID found');
        return;
      }

      const formData = new FormData();
      formData.append('id', variantInput.value);
      formData.append('quantity', quantityInput?.value || '1');

      // Add to cart
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        // Error occurred
        console.error('Buy Now error:', data.message);
        this.#isSubmitting = false;
        this.#buyNowButton?.classList.remove('is-loading');
        return;
      }

      // Success - redirect to checkout
      window.location.href = '/checkout';

    } catch (error) {
      console.error('Buy Now error:', error);
      this.#isSubmitting = false;
      this.#buyNowButton?.classList.remove('is-loading');
    }
  };

  /**
   * Handle variant updates to sync button state and price
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail.resource;

    if (!variant) {
      // No variant available
      this.#atcButton?.setAttribute('disabled', '');
      this.#buyNowButton?.setAttribute('disabled', '');
      return;
    }

    // Update button state
    if (variant.available) {
      this.#atcButton?.removeAttribute('disabled');
      this.#buyNowButton?.removeAttribute('disabled');
    } else {
      this.#atcButton?.setAttribute('disabled', '');
      this.#buyNowButton?.setAttribute('disabled', '');
    }

    // Update price display
    if (this.#priceElement && variant.price) {
      const formatter = new Intl.NumberFormat(window.Shopify?.locale || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      });
      this.#priceElement.textContent = formatter.format(variant.price / 100);
    }
  };

  /**
   * Animate the "Added" state on the ATC button
   */
  #animateAdded() {
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);

    this.#atcButton?.classList.add('is-added');

    this.#addedTimeout = setTimeout(() => {
      this.#atcButton?.classList.remove('is-added');
    }, ADDED_ANIMATION_DURATION);
  }
}

if (!customElements.get('sticky-atc-component')) {
  customElements.define('sticky-atc-component', StickyAtcComponent);
}
